/**
 * Utilities for Support Ticket notification routing, payload extraction, and role-based deep linking.
 */

/**
 * Determines whether a given notification is associated with a support ticket.
 * @param {Object} notif 
 * @returns {boolean}
 */
export function isTicketNotification(notif) {
  if (!notif) return false;

  const ticketTypes = ["new_ticket", "ticket_solved", "ticket_chat", "ticket_reopened"];
  if (ticketTypes.includes(notif.type)) return true;

  if (notif.ticketId || notif.ticketNumber) return true;

  if (typeof notif.key === "string" && (notif.key.startsWith("ticket_") || notif.key.startsWith("new_ticket_"))) {
    return true;
  }

  if (typeof notif.path === "string" && (notif.path.includes("ticketId") || notif.path.includes("ticketNumber") || notif.path.startsWith("/support"))) {
    return true;
  }

  if (typeof notif.message === "string" && (/TKT-\d+/i.test(notif.message) || /ticket/i.test(notif.message))) {
    return true;
  }

  return false;
}

/**
 * Extracts ticketId and ticketNumber from notification object attributes, keys, or messages.
 * Handles backwards compatibility with legacy notifications.
 * @param {Object} notif 
 * @returns {{ ticketId: string | null, ticketNumber: string | null }}
 */
export function extractTicketInfo(notif) {
  if (!notif) return { ticketId: null, ticketNumber: null };

  let ticketId = notif.ticketId || null;
  let ticketNumber = notif.ticketNumber || null;

  // Attempt to extract ticketId from key
  if (!ticketId && typeof notif.key === "string") {
    if (notif.key.startsWith("ticket_solved_")) {
      ticketId = notif.key.replace("ticket_solved_", "").trim();
    } else if (notif.key.startsWith("ticket_chat_")) {
      const parts = notif.key.split("_");
      // Format: ticket_chat_{ticketId}_{timestamp}
      if (parts.length >= 3 && parts[2]) {
        ticketId = parts[2].trim();
      }
    }
  }

  // Attempt to extract ticketNumber from message
  if (!ticketNumber && typeof notif.message === "string") {
    const match = notif.message.match(/TKT-\d+/i);
    if (match) {
      ticketNumber = match[0].toUpperCase();
    } else {
      const matchGeneral = notif.message.match(/Ticket\s+([A-Za-z0-9-_]+)/i);
      if (matchGeneral && matchGeneral[1]) {
        ticketNumber = matchGeneral[1].trim();
      }
    }
  }

  // Fallback: extract ticketNumber from key for new_ticket_{ticketNumber}
  if (!ticketNumber && typeof notif.key === "string" && notif.key.startsWith("new_ticket_")) {
    ticketNumber = notif.key.replace("new_ticket_", "").trim();
  }

  // Fallback: check query parameters if notif.path exists
  if (typeof notif.path === "string" && notif.path.includes("?")) {
    try {
      const qs = notif.path.split("?")[1];
      const params = new URLSearchParams(qs);
      if (!ticketId && params.get("ticketId")) ticketId = params.get("ticketId");
      if (!ticketNumber && params.get("ticketNumber")) ticketNumber = params.get("ticketNumber");
    } catch (e) {
      // Ignore URL parsing errors
    }
  }

  return { ticketId, ticketNumber };
}

/**
 * Resolves the target navigation path for a ticket notification based on user roles:
 * - Developer role -> `/support?ticketId={id}&ticketNumber={number}`
 * - All other roles -> `/profile?tab=tickets&ticketId={id}&ticketNumber={number}`
 * 
 * @param {Object} notif 
 * @param {Array<string> | string} userRoles 
 * @returns {string}
 */
export function getTicketNavigationPath(notif, userRoles = []) {
  const rolesArray = Array.isArray(userRoles)
    ? userRoles.map(r => String(r).toLowerCase().trim())
    : (userRoles ? [String(userRoles).toLowerCase().trim()] : []);

  const isDeveloper = rolesArray.includes("developer");
  const { ticketId, ticketNumber } = extractTicketInfo(notif);

  const params = new URLSearchParams();
  if (!isDeveloper) {
    params.set("tab", "tickets");
  }
  if (ticketId) {
    params.set("ticketId", ticketId);
  }
  if (ticketNumber) {
    params.set("ticketNumber", ticketNumber);
  }

  const queryString = params.toString() ? `?${params.toString()}` : "";
  return isDeveloper ? `/support${queryString}` : `/profile${queryString}`;
}
